import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { useAuth } from '../context/AuthContext';
import apiClient from '../utils/api';

// The insight engine's audit board. Not a user surface — this is where the
// machinery is held to account: denominators next to findings, skip reasons in
// plain English, the negative-control calibration check, and which of the
// pre-registered questions this record can actually answer.
//
// The page is deliberately dumb. Every panel arrives from the backend as
// {title, note, stats, rows}, and the backend refuses to emit a number without
// a sentence explaining it — so rendering is generic and the *sentence* is the
// primary content, with the figures beside it rather than instead of it.

const PANEL_ROW_LIMIT = 8;

const fmtNumber = (v) => (
  typeof v === 'number' && Number.isFinite(v)
    ? (Number.isInteger(v) ? v.toLocaleString() : v.toFixed(2))
    : String(v)
);

const STAT_LABELS = {
  n_candidates: 'questions possible',
  n_tested: 'answerable',
  n_discovered: 'cleared the bar',
  n_suppressed: 'dropped as duplicates',
  duration_s: 'seconds',
  days: 'days',
  columns: 'columns',
  columns_with_data: 'with data',
  columns_usable: 'testable',
  thin_days: 'thin days',
  dense_columns: 'usually present',
  window: 'days looked at',
  controls: 'controls wired in',
  tested_last_run: 'tested last sweep',
  best_q: 'best control q',
  breached_runs: 'breached sweeps',
  total: 'total',
  testable: 'answerable now',
  blocked: 'blocked',
  active: 'running',
  accounts: 'accounts',
  swept: 'swept at least once',
  runs: 'sweeps',
  total_discovered: 'findings',
  hunch: 'hunch',
  watching: 'watching',
  confirmed: 'confirmed',
  retired: 'retired',
};

const SKIPPED_STATS = new Set(['first_day', 'last_day', 'window_start',
  'window_end', 'holdout_start', 'ran_at']);

// ---------------------------------------------------------------------------

const Stat = ({ label, value }) => (
  <div className="rounded-lg bg-gray-100 dark:bg-surface-dark-raised px-3 py-2">
    <div className="text-lg font-semibold text-gray-900 dark:text-gray-50">
      {value === null || value === undefined ? '—' : fmtNumber(value)}
    </div>
    <div className="text-sm text-gray-700 dark:text-gray-300">{label}</div>
  </div>
);

const Row = ({ row }) => {
  // The colour is the only shorthand on the page, and it only ever marks a row
  // the engine itself flagged: blocked, thin, or a control that fired.
  const flagged = row.blocked || row.thin || row.control_breach;
  const good = row.usable === true || row.blocked === null;
  return (
    <li className="flex gap-3 py-2 border-b border-surface-border dark:border-surface-dark-border last:border-0">
      <span
        aria-hidden="true"
        className={`mt-2 h-2 w-2 shrink-0 rounded-full ${
          flagged ? 'bg-[#EF4444]' : good ? 'bg-[#10B981]' : 'bg-[#FFB800]'
        }`}
      />
      <span className="text-base text-gray-900 dark:text-gray-100">{row.note}</span>
    </li>
  );
};

const Panel = ({ panel }) => {
  const [expanded, setExpanded] = useState(false);
  const rows = expanded ? panel.rows : panel.rows.slice(0, PANEL_ROW_LIMIT);
  const stats = Object.entries(panel.stats || {})
    .filter(([k, v]) => !SKIPPED_STATS.has(k) && typeof v !== 'string');

  return (
    <Card className={panel.alarm ? 'border-[#EF4444] border-2' : ''}>
      <CardHeader className="pb-3">
        <CardTitle className="text-xl">
          {panel.alarm && <span className="mr-2 text-[#EF4444]">⚠</span>}
          {panel.title}
        </CardTitle>
        <p className="text-base leading-relaxed text-gray-800 dark:text-gray-200">
          {panel.note}
        </p>
      </CardHeader>
      <CardContent className="pt-0">
        {stats.length > 0 && (
          <div className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
            {stats.map(([k, v]) => (
              <Stat key={k} label={STAT_LABELS[k] || k.replace(/_/g, ' ')} value={v} />
            ))}
          </div>
        )}
        {panel.rows.length > 0 && (
          <>
            <ul>{rows.map((r, i) => <Row key={i} row={r} />)}</ul>
            {panel.rows.length > PANEL_ROW_LIMIT && (
              <Button
                variant="ghost"
                size="sm"
                className="mt-2"
                onClick={() => setExpanded((e) => !e)}
              >
                {expanded
                  ? 'Show fewer'
                  : `Show all ${panel.rows.length}`}
              </Button>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

// ---------------------------------------------------------------------------

const AdminInsightPage = () => {
  const { user, loading: authLoading } = useAuth();
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sweeping, setSweeping] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get('/api/admin/insight/board');
      setBoard(data);
      setError(null);
    } catch (err) {
      setError(err.response?.status === 403
        ? 'Admin access required.'
        : 'Could not load the board.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!authLoading && user?.is_admin) load();
    else if (!authLoading) setLoading(false);
  }, [authLoading, user, load]);

  const sweep = async () => {
    setSweeping(true);
    try {
      // A sweep is thousands of tests against a couple of years of history; it
      // outlives the client's default timeout on any record worth sweeping.
      const { data } = await apiClient.post('/api/admin/insight/sweep', null,
        { timeout: 180000 });
      toast.success(data.note, { duration: 8000 });
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Sweep failed.');
    } finally {
      setSweeping(false);
    }
  };

  if (authLoading || loading) {
    return <div className="p-6 text-base text-gray-700 dark:text-gray-300">Loading…</div>;
  }
  if (!user?.is_admin) {
    return <div className="p-6 text-base text-gray-700 dark:text-gray-300">Admin access required.</div>;
  }
  if (error) {
    return <div className="p-6 text-base text-gray-700 dark:text-gray-300">{error}</div>;
  }

  return (
    <div className="mx-auto max-w-4xl px-4 py-6 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-50">
            Insight engine
          </h1>
          <p className="text-base text-gray-700 dark:text-gray-300">
            What the engine looked at, what it refused to say, and why.
          </p>
        </div>
        <Button onClick={sweep} disabled={sweeping}>
          {sweeping ? 'Sweeping…' : 'Sweep now'}
        </Button>
      </div>

      {board?.alarm && (
        <Card className="border-2 border-[#EF4444]">
          <CardContent className="p-4 text-base text-gray-900 dark:text-gray-100">
            <strong>A negative control cleared the bar.</strong> That is not a
            finding — it means the threshold is too low, and every other result
            from the affected sweep is suspect until it is explained.
          </CardContent>
        </Card>
      )}

      {(board?.panels || []).map((p) => <Panel key={p.key} panel={p} />)}
    </div>
  );
};

export default AdminInsightPage;
